import type { ClaimStrategyResponse } from "@patent-studio/api-types";
import { useState } from "react";
import { TriangleAlert } from "lucide-react";
import { FoldingCard } from "@/components/patterns/FoldingCard";
import { useCarriedItem } from "@/components/patterns/SortableList";
import { cn } from "@/lib/utils";
import { useDisclosure } from "@/components/ui/Disclosure";
import { StatusPill } from "@/components/ui/StatusPill";
import { VerdictBadge } from "@/components/shared/VerdictBadge";
import { unplacedAnchor } from "./ClaimStrategyFooter";
import { PositionOptions } from "./PositionOptions";
import { positionItem } from "./claim-lanes";
import { useClaimSetMoments } from "./claim-set-moments";

const NOTE =
  "The assistant could not place these with confidence. Place any of them into a claim yourself, or leave them. Nothing here is discarded.";
const ALL_PLACED = "Every agreed position is placed in a claim.";

/** One position waiting to be placed, marked while it has just come back. */
function UnplacedRow({
  taskId,
  stage,
  position,
  editable,
  returned,
}: {
  taskId: string;
  stage: ClaimStrategyResponse;
  position: ClaimStrategyResponse["unplaced"][number];
  editable: boolean;
  returned: boolean;
}) {
  const carried = useCarriedItem({
    id: positionItem(position.position_id),
    type: "position",
    label: `Drag position ${String(position.number)} onto a claim or the claim set`,
  });
  return (
    <li
      ref={editable ? carried.ref : undefined}
      id={unplacedAnchor(position.position_id)}
      tabIndex={-1}
      data-testid={`unplaced-${position.position_id}`}
      data-returned={returned || undefined}
      className={cn(
        "flex flex-wrap items-center gap-2.5 border-t border-border-subtle py-2.5 first:border-t-0 focus-visible:outline-none data-returned:animate-returned data-returned:rounded-sm motion-reduce:animate-none",
        carried.chrome,
      )}
    >
      {editable && carried.handle}
      <span className="min-w-0 flex-1 text-[12.8px] text-foreground">
        {position.title}
      </span>
      <VerdictBadge verdict={position.verdict} size="chip" />
      <PositionOptions
        taskId={taskId}
        position={position}
        stage={stage}
        editable={editable}
      />
    </li>
  );
}

/**
 * Every confirmed position no claim names. A derivation the server serves, so a
 * position confirmed after the proposal, or returned by a removed claim, is
 * here without anything having put it here.
 */
export function HoldingTray({
  taskId,
  stage,
  editable,
}: {
  taskId: string;
  stage: ClaimStrategyResponse;
  /** Whether the stage is open to writes, so a position can be placed. */
  editable: boolean;
}) {
  const { unplaced } = stage;
  const fold = useDisclosure();
  const { returned } = useClaimSetMoments();
  // Opened on the render that sees positions come back, so the marks are seen.
  const [seen, setSeen] = useState(returned);
  if (returned !== seen) {
    setSeen(returned);
    if (returned.size > 0) fold.setOpen(true);
  }
  return (
    <FoldingCard
      fold={fold}
      testId="holding-tray"
      className="mt-3.5 border-warning-border"
      bodyClassName="border-t-0 pt-0.5 pb-3.5"
      lead={
        <span className="flex w-[22px] flex-none justify-center text-warning">
          <TriangleAlert aria-hidden className="size-[17px]" />
        </span>
      }
      title={
        <span className="text-[13.5px] leading-[1.3] font-extrabold text-warning-strong">
          Positions not yet placed
        </span>
      }
      meta={
        <StatusPill
          tone="neutral"
          size="chip"
          plain
          data-testid="unplaced-count"
          // The design's count is quieter than a state: the sunken ground and the subtle caption.
          className="bg-surface-sunken text-subtle-foreground"
        >
          {unplaced.length}
        </StatusPill>
      }
    >
      {unplaced.length === 0 ? (
        <p className="pt-2 text-sm text-muted-foreground">{ALL_PLACED}</p>
      ) : (
        <>
          <ul>
            {unplaced.map((position) => (
              <UnplacedRow
                key={position.position_id}
                taskId={taskId}
                stage={stage}
                position={position}
                editable={editable}
                returned={returned.has(position.position_id)}
              />
            ))}
          </ul>
          <p className="mt-[9px] text-[11.5px] leading-normal text-warning-strong">
            {NOTE}
          </p>
        </>
      )}
    </FoldingCard>
  );
}
